import { FiBook } from 'react-icons/fi';

export const commonVerbsData = {
  courseId: 'common-verbs',
  courseType: 'vocabulary',
  title: 'Common Verbs',
  icon: FiBook,
  overview: {
    impact: 'Build a foundation of essential action words',
    focus: 'High-frequency verbs for everyday communication',
    uses: 'Speaking Parts 1-2, basic writing',
    duration: '20 minutes'
  },
  sections: [
    {
      title: 'The 30 Most Common English Verbs',
      content: `<p>These verbs appear in nearly every English conversation:</p>`,
      table: {
        headers: [
          'Verb',
          'Past',
          'Example'
        ],
        rows: [
          [
            'be',
            'was/were',
            'I am a student.'
          ],
          [
            'have',
            'had',
            'She has two brothers.'
          ],
          [
            'do',
            'did',
            'I do my homework daily.'
          ],
          [
            'say',
            'said',
            'He said hello.'
          ],
          [
            'go',
            'went',
            'We go to school.'
          ],
          [
            'get',
            'got',
            'I got a new book.'
          ],
          [
            'make',
            'made',
            'She makes delicious food.'
          ],
          [
            'know',
            'knew',
            'I know the answer.'
          ],
          [
            'think',
            'thought',
            'I think it\'s important.'
          ],
          [
            'take',
            'took',
            'He takes the bus.'
          ]
        ]
      }
    },
    {
      title: 'Collocations with Common Verbs',
      content: `<p>Verbs combine with specific words. Learn these natural pairs:</p>`,
      points: [
        'make: a decision, a mistake, progress, an effort, friends',
        'do: homework, research, business, the dishes, your best',
        'take: a break, notes, a photo, a course, responsibility',
        'have: a meeting, fun, a problem, breakfast, a shower',
        'get: ready, married, lost, better, a job'
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Right Verb',
      type: 'select',
      instructions: 'Select make, do, take, or have.',
      questions: [
        {
          text: 'She needs to ___ a decision soon.',
          options: [
            'make',
            'do',
            'take'
          ],
          answer: 'make',
          explanation: 'We say \'make a decision\', not \'do a decision\'.'
        },
        {
          text: 'I always ___ my homework before dinner.',
          options: [
            'do',
            'make',
            'take'
          ],
          answer: 'do',
          explanation: 'We say \'do homework\'.'
        },
        {
          text: 'Let\'s ___ a break.',
          options: [
            'take',
            'have',
            'make'
          ],
          answer: 'take',
          explanation: 'We say \'take a break\'.'
        },
        {
          text: 'Did you ___ fun at the party?',
          options: [
            'have',
            'make',
            'do'
          ],
          answer: 'have',
          explanation: 'We say \'have fun\'.'
        }
      ]
    },
    {
      title: 'Past Tense Forms',
      type: 'select',
      instructions: 'Choose the correct past tense form of the verb.',
      questions: [
        {
          text: 'What is the past tense of \'go\'?',
          options: ['went', 'goed', 'gone', 'going'],
          answer: 'went',
          explanation: '\'Go\' is irregular: go → went → gone.'
        },
        {
          text: 'What is the past tense of \'make\'?',
          options: ['made', 'maked', 'maken', 'making'],
          answer: 'made',
          explanation: '\'Make\' is irregular: make → made → made.'
        },
        {
          text: 'What is the past tense of \'think\'?',
          options: ['thought', 'thinked', 'thinkt', 'thunk'],
          answer: 'thought',
          explanation: '\'Think\' is irregular: think → thought → thought.'
        },
        {
          text: 'What is the past tense of \'get\'?',
          options: ['got', 'getted', 'gat', 'gotten'],
          answer: 'got',
          explanation: '\'Get\' is irregular: get → got → got/gotten.'
        }
      ]
    },
    {
      title: 'Complete the Collocation',
      type: 'fill-in',
      instructions: 'Type the correct verb (make, do, take, or have) to complete each collocation.',
      questions: [
        {
          text: '___ a decision',
          answer: 'make',
          explanation: 'The correct collocation is \'make a decision\'.'
        },
        {
          text: '___ your best',
          answer: 'do',
          explanation: 'The correct collocation is \'do your best\'.'
        },
        {
          text: '___ a break',
          answer: 'take',
          explanation: 'The correct collocation is \'take a break\'.'
        },
        {
          text: '___ fun',
          answer: 'have',
          explanation: 'The correct collocation is \'have fun\'.'
        }
      ]
    },
    {
      title: 'Use Verbs in Context',
      type: 'writing',
      instructions: 'Write a full sentence using the given verb collocation. Use correct grammar and natural phrasing.',
      questions: [
        {
          text: 'Write a sentence using \'make progress\'.',
          answer: 'make progress',
          explanation: 'Example: \'I have made a lot of progress with my English this semester.\' The collocation \'make progress\' means to improve or advance.'
        },
        {
          text: 'Write a sentence using \'take responsibility\'.',
          answer: 'take responsibility',
          explanation: 'Example: \'Students should take responsibility for their own learning.\' The collocation \'take responsibility\' means to accept duty for something.'
        },
        {
          text: 'Write a sentence using \'do research\'.',
          answer: 'do research',
          explanation: 'Example: \'She did extensive research before writing her essay.\' We say \'do research\', not \'make research\'.'
        }
      ]
    }
  ],
  tips: [
    'Learn make/do/take/have collocations — they\'re tested in IELTS',
    'Practice using verbs in full sentences, not isolation',
    'Learn past forms of irregular verbs',
    'Focus on the 50 most common verbs first'
  ],
  dailyPractice: [
    'Practice common verbs concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Common Verbs: High-frequency verbs with collocations (make/do/take/have). Foundation for IELTS fluency.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...commonVerbsData,
      title: commonVerbsData.title + ' (Foundations)',
      overview: { ...commonVerbsData.overview, duration: '15-20 minutes' },
      sections: commonVerbsData.sections.slice(0, Math.max(2, Math.ceil(commonVerbsData.sections.length / 2))),
      exercises: commonVerbsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...commonVerbsData,
      title: commonVerbsData.title + ' (Advanced)',
      overview: { ...commonVerbsData.overview, duration: '30-35 minutes' },
      sections: commonVerbsData.sections,
      exercises: commonVerbsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(commonVerbsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return commonVerbsData;
}
